import { ProcessCellForExportParams } from 'ag-grid-enterprise'
import { getDecimalsFromParams } from './gridUtils'
import { VariableType } from '../../../../components/Table/types'

/**
 * Sentinel value placed in group header row cells during copy in accountMapGrid.
 * processDataFromClipboard uses this to strip group rows before paste,
 * so values land on the correct leaf rows even when the selection spans
 * multiple row groups.
 * Only used in grids where group rows are non-editable headers (e.g. AccountMapRuleGrid).
 */
export const GROUP_ROW_SENTINEL = '__GROUP_ROW__'

/**
 * Process cell value for clipboard with locale support
 * - Financial statements (reference method): Multiplies by 100 for display (0.15 -> 15)
 * - Driver data percentage: Multiplies by 100 (0.5 -> 50)
 * - Driver data absolute: Rounds based on decimals from row data or column config
 *
 * Uses shared getDecimalsFromParams for consistency with formatters and parsers:
 * - row.style.decimals (highest priority)
 * - column.cellEditorParams.decimals
 * - Type-based defaults (absolute: 0, percentage: 1, ratio: 1)
 *
 * @param _locale - User's locale (reserved for future use)
 * @returns Function that processes cell values for clipboard
 */
export const createProcessCellForClipboard = (_locale: string) => {
  return (params: ProcessCellForExportParams): string | number => {
    const colDef = params.column.getColDef()
    const rowData = params.node?.data

    if (typeof params.value === 'number') {
      // Check percentage type from variableType, dataType, or budgetingMethod.method (financial statements)
      const valueType = rowData?.variableType || rowData?.dataType
      const isReferenceMethod = rowData?.budgetingMethod?.method === 'reference'
      if (valueType === 'percentage' || isReferenceMethod) {
        const decimals = getDecimalsFromParams({ data: rowData, colDef }, VariableType.percentage)
        return Number((params.value * 100).toFixed(decimals))
      }

      if (colDef.cellDataType === 'number') {
        const decimals = getDecimalsFromParams({ data: rowData, colDef }, VariableType.absolute)
        return Number(params.value.toFixed(decimals))
      }
    }

    return params.value ?? ''
  }
}

/**
 * Process cell value when pasting from clipboard
 * Converts percentage display values back to decimal for percentage rows:
 * - Financial statements (reference method): 15 -> 0.15
 * - Driver data percentage: 50 -> 0.5
 * - Other rows: value passed through unchanged
 */
export const createProcessCellFromClipboard = () => {
  return (params: ProcessCellForExportParams): string | number => {
    const rowData = params.node?.data
    const isReferenceMethod = rowData?.budgetingMethod?.method === 'reference'
    const valueType = rowData?.variableType || rowData?.dataType

    if (isReferenceMethod || valueType === 'percentage') {
      const raw = String(params.value).replace(/[%\s]/g, '').replace(/,/g, '.')
      const parsed = parseFloat(raw)
      if (!Number.isNaN(parsed)) {
        return parsed / 100
      }
    }

    // For other cells: parse numeric strings to numbers to maintain type compatibility.
    // AG Grid previously auto-converted strings to numbers for cellDataType: 'number' columns,
    // but when processCellFromClipboard is set it bypasses that auto-conversion.
    if (typeof params.value === 'string' && params.value.trim() !== '') {
      const num = Number(params.value)
      if (!Number.isNaN(num)) {
        return num
      }
    }

    return params.value
  }
}

/**
 * Parse a string value that may contain locale-formatted numbers
 * Handles thousands separators (spaces, non-breaking spaces, commas, dots)
 * and decimal separators (comma or dot)
 */
export const parseLocaleNumber = (value: string): number | string => {
  if (typeof value !== 'string') return value

  const trimmed = value.trim()
  if (trimmed === '') return ''

  // Remove whitespace used as thousands separators
  let normalized = trimmed.replace(/[\s\u00A0]/g, '')

  // Count occurrences of separators
  const commaCount = (normalized.match(/,/g) || []).length
  const dotCount = (normalized.match(/\./g) || []).length
  const lastComma = normalized.lastIndexOf(',')
  const lastDot = normalized.lastIndexOf('.')

  // Detect format based on separator positions and counts
  if (dotCount > 1 || (dotCount >= 1 && commaCount >= 1 && lastComma > lastDot)) {
    // EU format: multiple dots OR dots before comma means dots are thousands separators
    // Examples: "1.234.567" or "1.234.567,89" or "1.234,56"
    normalized = normalized.replace(/\./g, '').replace(',', '.')
  } else if (commaCount > 1 || (commaCount >= 1 && dotCount >= 1 && lastDot > lastComma)) {
    // US format: multiple commas OR commas before dot means commas are thousands separators
    // Examples: "1,234,567" or "1,234,567.89" or "1,234.56"
    normalized = normalized.replace(/,/g, '')
  } else if (commaCount === 1 && dotCount === 0) {
    // Single comma, no dot - check if it looks like thousands separator (followed by exactly 3 digits)
    // Guard: leading "0" means it cannot be a thousands group (e.g. "0,186" is 0.186, not 186)
    if (/,\d{3}$/.test(normalized) && !normalized.startsWith('0')) {
      // Likely thousands separator: "1,234" -> 1234
      normalized = normalized.replace(',', '')
    } else {
      // Likely decimal comma: "12,5" -> 12.5
      normalized = normalized.replace(',', '.')
    }
  }
  // Single dot (dotCount === 1 && commaCount === 0): dot is always the JS decimal separator.
  // Finnish locale uses spaces for thousands (not dots), so "12.643" means 12.643, not 12643.
  // Removing the thousands-separator heuristic for dots prevents misinterpreting values like
  // "0.186" or "12.643" that come from processCellForClipboard (which uses toFixed with dot).

  const parsed = Number(normalized)
  return Number.isNaN(parsed) ? value : parsed
}

/**
 * Process clipboard data when pasting into the grid
 * Parses locale-formatted numbers (e.g., "7 052 840" or "1.234,56") into actual numbers
 */
export const createProcessDataFromClipboard = () => {
  return (params: { data: string[][] }): string[][] | null => {
    if (!params.data) return null

    return params.data.map(row =>
      row.map(cell => {
        const parsed = parseLocaleNumber(cell)
        return String(parsed)
      })
    )
  }
}

/**
 * Process clipboard data for grids with group rows that should be skipped during paste.
 * Filters out rows where all cells are the GROUP_ROW_SENTINEL value.
 * Use in grids where group rows are non-editable headers (e.g. AccountMapRuleGrid).
 */
export const createProcessDataFromClipboardWithGroupRows = () => {
  return (params: { data: string[][] }): string[][] | null => {
    if (!params.data) return null

    return params.data
      .filter(row => !row.every(cell => cell === GROUP_ROW_SENTINEL))
      .map(row =>
        row.map(cell => {
          const parsed = parseLocaleNumber(cell)
          return String(parsed)
        })
      )
  }
}

/**
 * Legacy export for backwards compatibility (uses Finnish locale as default)
 * @deprecated Use createProcessCellForClipboard with locale parameter instead
 */
export const processCellForClipboard = createProcessCellForClipboard('fi-FI')
